import React, { useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/*
  EntityCard: the shared grid-card shell for Categories, Accounts and Goals —
  optional full-bleed image banner, identity row (drag handle, color dot,
  name + meta, type chips, kebab menu), headline stat with optional delta
  pill, a chart slot, and a footer slot (the AI InsightsPanel). Purely
  presentational: the wrappers own the react-dnd wiring and pass
  `cardRef`/`isDragging` through.

  While dragging, the source card becomes the same Trello-style placeholder
  slot the old rows used (dashed border, grey fill, hidden content).
*/

const EntityCard = ({
  cardRef,
  handlerId,
  isDragging = false,
  canDrag = true,
  banner = null,
  color,
  name,
  meta = null,
  chips = null,
  menuItems = [],
  statLabel,
  statValue,
  delta = null,
  chart = null,
  footer = null,
  selectable = false,
  selected = false,
  onToggleSelect,
  dimmed = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Card
      ref={cardRef}
      data-handler-id={handlerId}
      elevation={2}
      sx={{
        p: 2,
        pb: footer ? 0.5 : 2,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        opacity: dimmed && !isDragging ? 0.65 : 1,
        bgcolor: isDragging ? 'grey.100' : 'background.paper',
        outline: isDragging ? '1px dashed' : 'none',
        outlineColor: 'grey.400',
        cursor: canDrag ? 'grab' : 'default',
        '&:active': { cursor: canDrag ? 'grabbing' : 'default' },
        '&:hover .entity-hover-controls': { maxWidth: 64, opacity: 1, mr: 0 },
      }}
    >
      <Box sx={{ opacity: isDragging ? 0 : 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        {/* Optional full-bleed image banner (e.g. a goal photo) */}
        {banner && (
          <Box
            sx={{
              height: 120,
              mx: -2,
              mt: -2,
              mb: 1.5,
              backgroundImage: `url(${banner})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              flexShrink: 0,
            }}
          />
        )}
        {/* Identity row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {/* Trello-style: the drag handle and checkbox live in a zero-width
              slot that slides open on hover (staying open while checked), so
              the color dot and name sit flush left the rest of the time. The
              negative margin cancels the row's flex gap while collapsed. */}
          {(canDrag || selectable) && (
            <Box
              className="entity-hover-controls"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'max-width 150ms ease, opacity 150ms ease, margin-right 150ms ease',
                ...(selected
                  ? { maxWidth: 64, opacity: 1, mr: 0 }
                  : { maxWidth: 0, opacity: 0, mr: -1 }),
                '&:focus-within': { maxWidth: 64, opacity: 1, mr: 0 },
              }}
            >
              {canDrag && (
                <DragIndicatorIcon
                  className="entity-drag-handle"
                  color="action"
                  sx={{ fontSize: 18, opacity: 0.55, flexShrink: 0 }}
                />
              )}
              {selectable && (
                <Checkbox
                  className="entity-select-checkbox"
                  checked={selected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggleSelect && onToggleSelect()}
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}
                />
              )}
            </Box>
          )}
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color || 'grey.400', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={name}>
              <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
                {name}
              </Typography>
            </Tooltip>
            {meta && (
              <Tooltip title={meta}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.75rem' }}>
                  {meta}
                </Typography>
              </Tooltip>
            )}
          </Box>
          {chips}
          {menuItems.length > 0 && (
            <>
              <IconButton
                size="small"
                aria-label={`${name} actions`}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
                sx={{ color: 'text.secondary', flexShrink: 0, mr: -0.75 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                onClick={(e) => e.stopPropagation()}
              >
                {menuItems.map((item) => {
                  const menuItem = (
                    <MenuItem
                      key={item.key}
                      disabled={item.disabled}
                      onClick={() => {
                        setMenuAnchor(null);
                        item.onClick();
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {item.icon && <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>}
                      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>{item.label}</ListItemText>
                    </MenuItem>
                  );
                  // span keeps tooltips working on disabled items
                  return item.tooltip ? (
                    <Tooltip key={item.key} title={item.tooltip}>
                      <span>{menuItem}</span>
                    </Tooltip>
                  ) : (
                    menuItem
                  );
                })}
              </Menu>
            </>
          )}
        </Box>

        {/* Headline stat */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', mt: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.2 }}>
              {statLabel}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 }}
            >
              {statValue}
            </Typography>
          </Box>
          {delta}
        </Box>

        {/* Chart slot */}
        {chart && <Box sx={{ mt: 1 }}>{chart}</Box>}

        {/* Footer slot (AI insights) */}
        {footer}
      </Box>
    </Card>
  );
};

export default EntityCard;
